import React from 'react';
import { Edit, Trash2, Users } from 'lucide-react';
import EmptyState from '../Common/EmptyState';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';
import { useTenant } from '../../contexts/TenantContext';
import { partyNoun } from '../../utils/tenantProfile';

const ClientTable = ({ clients, isLoading, onEdit, onDelete, resolveDocument, resolvePersonType, onCreate }) => {
  const { tenant } = useTenant();
  const party = partyNoun(tenant);
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!clients?.length) {
    return (
      <EmptyState
        icon={Users}
        title={`Nenhum ${party.one} encontrado`}
        description={`Ajuste os filtros ou cadastre um novo ${party.one}.`}
        action={
          onCreate ? (
            <Can permission={P.IDENTITY_ACCESS}>
              <button type="button" className="btn btn-primary" onClick={onCreate}>
                Novo {party.one}
              </button>
            </Can>
          ) : null
        }
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documento</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo de Pessoa</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CTF</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {clients.map((client) => (
            <tr key={client.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-gray-900">{client.name}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">
                  {resolveDocument ? resolveDocument(client) : (client.document || client.cpf || client.cnpj)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  (resolvePersonType ? resolvePersonType(client) : client.type) === 'INDIVIDUAL' 
                    ? 'bg-primary-100 text-primary-700' 
                    : 'bg-info-100 text-info-700'
                }`}>
                  {(resolvePersonType ? resolvePersonType(client) : client.type) === 'INDIVIDUAL' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm text-gray-900">{client.ctf || '—'}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  client.is_active 
                    ? 'bg-success-100 text-success-700' 
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {client.is_active ? 'Ativo' : 'Inativo'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <Can permission={P.IDENTITY_ACCESS}>
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit(client)}
                      className="text-primary-600 hover:text-primary-900 p-1 rounded hover:bg-primary-50"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(client.id)}
                      className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                      title="Excluir"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Can>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ClientTable;